/**
 * Standings, the tiebreak chain, and championship flight seeding.
 */

import { breakTie, type TiebreakContext } from './rank'
import { emptyCounters } from './scoring'
import type {
  Award,
  Flight,
  PlayerId,
  Settlement,
  TiebreakChain,
  TiebreakCounters,
  TiebreakResolution,
  UnitTab,
} from './types'

export interface StandingRow {
  playerId: PlayerId
  position: number
  points: number
  cash: number
  counters: TiebreakCounters
  /** Finishing place per match id, for the `placeInRound` tiebreak. */
  places: Record<string, number>
  /** Set when this player's position was decided by a tiebreak, for display. */
  tiebreakNote: string | null
}

export interface Standings {
  rows: StandingRow[]
  tiebreaks: TiebreakResolution[]
}

function addCounters(a: TiebreakCounters, b: TiebreakCounters): TiebreakCounters {
  return {
    eagles: a.eagles + b.eagles,
    birdies: a.birdies + b.birdies,
    pars: a.pars + b.pars,
    bogeys: a.bogeys + b.bogeys,
    worse: a.worse + b.worse,
  }
}

/**
 * Roll every settled match into a single table.
 *
 * `matchSeqById` lets the `placeInRound` tiebreak step find a player's finishing
 * place in a specific round — the sheet breaks a seeding tie on where you
 * finished in round 5.
 */
export function computeStandings(
  playerIds: PlayerId[],
  settlements: Settlement[],
  tabsByMatch: Record<string, UnitTab[]>,
  chain: TiebreakChain,
  tb: TiebreakContext,
  matchSeqById: Record<string, number> = {},
  scope = 'standings',
): Standings {
  const points: Record<PlayerId, number> = {}
  const cash: Record<PlayerId, number> = {}
  const counters: Record<PlayerId, TiebreakCounters> = {}
  const places: Record<PlayerId, Record<string, number>> = {}

  for (const pid of playerIds) {
    points[pid] = 0
    cash[pid] = 0
    counters[pid] = emptyCounters()
    places[pid] = {}
  }

  const applyAward = (a: Award) => {
    if (!(a.playerId in points)) return
    points[a.playerId]! += a.points
    cash[a.playerId]! += a.cash
    if (a.place != null) places[a.playerId]![a.matchId] = a.place
  }

  for (const s of settlements) for (const a of s.awards) applyAward(a)

  // Counters accumulate across every match, since the chain is a season-long
  // tally rather than a per-match one.
  for (const tabs of Object.values(tabsByMatch)) {
    for (const tab of tabs) {
      for (const [pid, c] of Object.entries(tab.counters)) {
        if (counters[pid]) counters[pid] = addCounters(counters[pid]!, c)
      }
    }
  }

  const placeInRound = (roundSeq: number, playerId: PlayerId): number | null => {
    const matchId = Object.keys(matchSeqById).find((id) => matchSeqById[id] === roundSeq)
    if (!matchId) return null
    return places[playerId]?.[matchId] ?? null
  }

  const sorted = [...playerIds].sort((a, b) => points[b]! - points[a]!)
  const buckets: PlayerId[][] = []
  for (const pid of sorted) {
    const last = buckets[buckets.length - 1]
    if (last && points[last[0]!] === points[pid]) last.push(pid)
    else buckets.push([pid])
  }

  const ordered: PlayerId[] = []
  const tiebreaks: TiebreakResolution[] = []
  const notes: Record<PlayerId, string> = {}

  for (const bucket of buckets) {
    if (bucket.length === 1) {
      ordered.push(bucket[0]!)
      continue
    }
    const { order, resolutions } = breakTie(
      scope,
      bucket.map((pid) => ({
        playerId: pid,
        counters: counters[pid]!,
        netTotal: 0,
      })),
      chain,
      { ...tb, placeInRound },
    )
    tiebreaks.push(...resolutions)
    for (const pid of order) {
      ordered.push(pid)
      /*
       * The DEEPEST resolution that named him. Nested resolutions come after
       * their parent, so the last match is the step that actually placed a
       * man — "coin flip" for the pair the birdie count could not split, not
       * the birdie count that split everybody else.
       */
      const own = [...resolutions].reverse().find((r) => r.contenders.includes(pid))
      notes[pid] = own?.detail ?? resolutions[0]!.detail
    }
  }

  return {
    rows: ordered.map((pid, i) => ({
      playerId: pid,
      position: i + 1,
      points: points[pid]!,
      cash: cash[pid]!,
      counters: counters[pid]!,
      places: places[pid]!,
      tiebreakNote: notes[pid] ?? null,
    })),
    tiebreaks,
  }
}

/**
 * Split the field into championship flights by seeding position.
 *
 * The 2025 sheet seeds 1-4 into Flight 1, 5-8 into Flight 2 and 9-12 into
 * Flight 3, which is why flights are expressed as inclusive seed ranges rather
 * than a fixed size — a field that is not a multiple of four still has to land
 * somewhere sensible.
 */
export function assignFlights(
  standings: Standings,
  flights: Flight[],
): { flight: Flight; playerIds: PlayerId[] }[] {
  return flights.map((flight) => ({
    flight,
    playerIds: standings.rows
      .filter((r) => r.position >= flight.fromSeed && r.position <= flight.toSeed)
      .map((r) => r.playerId),
  }))
}

/**
 * What is wrong with a set of championship flights, in plain words.
 *
 * `assignFlights` takes the ranges at their word: it hands each flight the
 * seeds inside its range and asks no questions. So a gap silently drops a
 * player out of every flight and pays him nothing, and an overlap silently puts
 * him in two and pays him twice — both from a screen whose only defense was a
 * paragraph of prose asking the admin to be careful. The championship purse is
 * four thousand dollars.
 *
 * Returns one message per problem, worst first, and an empty list when the
 * ranges tile the field exactly once.
 */
export function flightProblems(flights: Flight[], playerCount: number): string[] {
  const out: string[] = []
  if (flights.length === 0) return out

  for (const f of flights) {
    if (f.fromSeed < 1) out.push(`${f.name} starts at seed ${f.fromSeed} — seeds start at 1.`)
    if (f.toSeed < f.fromSeed) {
      out.push(`${f.name} runs from ${f.fromSeed} to ${f.toSeed}, which is backwards.`)
    }
  }

  // Counted per seed rather than by comparing ranges pairwise, so a three-way
  // overlap is one clear message about seed 5 instead of three about ranges.
  const covering = new Map<number, string[]>()
  for (let seed = 1; seed <= playerCount; seed += 1) {
    covering.set(
      seed,
      flights.filter((f) => seed >= f.fromSeed && seed <= f.toSeed).map((f) => f.name),
    )
  }

  const uncovered = [...covering.entries()].filter(([, f]) => f.length === 0).map(([s]) => s)
  if (uncovered.length > 0) {
    out.push(
      `Seed${uncovered.length === 1 ? '' : 's'} ${uncovered.join(', ')} ${
        uncovered.length === 1 ? 'is' : 'are'
      } in no flight and would be paid nothing.`,
    )
  }

  for (const [seed, names] of covering) {
    if (names.length > 1) {
      out.push(`Seed ${seed} is in ${names.join(' and ')} — they would be paid twice.`)
    }
  }

  return out
}

/**
 * Whether the standings yet say anything about who is where.
 *
 * The championship flights are seeded off this table, so before anything has
 * been settled every player sits on nothing and the "order" is whatever the
 * tiebreak chain produces from a twelve-way tie. That is not a projection, it
 * is noise wearing a projection's clothes, and showing it beside a flight worth
 * a thousand dollars invites somebody to believe it.
 *
 * Deliberately the weakest possible test — a single settled match is enough to
 * make the ranking mean something, and from there the honest thing is to show
 * the projection and label it clearly rather than to withhold it.
 */
export function hasSeedingBasis(standings: Standings): boolean {
  return standings.rows.some((r) => r.points !== 0)
}

/** Running money position, including the buy-in. */
export function settlementLedger(
  standings: Standings,
  buyIn: number,
): { playerId: PlayerId; won: number; buyIn: number; net: number }[] {
  return standings.rows.map((r) => ({
    playerId: r.playerId,
    won: r.cash,
    buyIn,
    net: r.cash - buyIn,
  }))
}

/**
 * Re-order the finished week by where each man came in his own flight.
 *
 * Points decide the seeding, and the seeding decides the flights — but once the
 * championship has been played the points are a spent currency. The week is
 * settled by the final: Flight 1 finishes 1st to 4th, Flight 2 fifth to eighth,
 * Flight 3 ninth to twelfth. A man who wins Flight 2 has beaten his four, and
 * that is worth more than the season-long points total he arrived with.
 *
 * Applied ONLY once the championship card is signed. Before that this would
 * reshuffle the table on every hole of the last round, and the points ranking
 * is still the live question because it is what decides the flights.
 *
 * Anyone outside the flights — somebody who won money earlier and has since
 * left the roster — keeps his existing order behind the men who played.
 */
export function rankByFlightFinish(
  standings: Standings,
  flights: { flight: Flight; playerIds: PlayerId[] }[],
  /** Finishing place within his flight, from the championship's own settlement. */
  placeOf: (playerId: PlayerId) => number | null,
): Standings {
  const byId = new Map(standings.rows.map((r) => [r.playerId, r]))
  const ordered: PlayerId[] = []

  /*
   * A man is placed once, whatever the flight ranges say.
   *
   * `flightProblems` warns about overlapping seed ranges in Setup and the
   * start gate blocks on it — but the ranges stay editable all week, and by
   * Thursday the week is long started. A seed covered by two flights was
   * pushed twice, so the standings came back with thirteen rows for twelve men
   * and the Board's ledger totalled $11,650 against a $10,200 pot. The
   * championship purse itself was right; only the table double-counted.
   */
  const seen = new Set<PlayerId>()
  for (const { playerIds } of flights) {
    const inFlight = playerIds.filter((pid) => byId.has(pid) && !seen.has(pid))
    for (const pid of inFlight) seen.add(pid)
    inFlight.sort((a, b) => {
      const pa = placeOf(a)
      const pb = placeOf(b)
      // No place yet — a man who did not post a card — sits at the foot of his
      // flight rather than silently jumping to the top of it.
      if (pa == null && pb == null) return 0
      if (pa == null) return 1
      if (pb == null) return -1
      return pa - pb
    })
    ordered.push(...inFlight)
  }

  const placed = new Set(ordered)
  for (const r of standings.rows) if (!placed.has(r.playerId)) ordered.push(r.playerId)

  return {
    ...standings,
    rows: ordered.map((pid, i) => ({ ...byId.get(pid)!, position: i + 1 })),
  }
}
